import React, { useEffect, useState, useMemo } from 'react'
import {
	Shield,
	Users,
	Plus,
	Eye,
	Edit2,
	Trash2,
	Copy,
	RotateCcw,
	Filter,
	ArrowUpDown,
	Lock,
} from 'lucide-react'
import { DataTable, SearchBar, Select, Button, ConfirmDialog, PageHeader } from '@components/common'
import { roleService } from '@services/roleService'
import { useAuth } from '@context/AuthContext'
import toast from 'react-hot-toast'
import CompanyRoleForm from './CompanyRoleForm'
import CompanyRoleView from './CompanyRoleView'
import './CompanyRoles.scss'

/**
 * Check if a role is a system default role
 * Handles string "1"/"0", number 1/0, and boolean true/false
 */
const isSystemRole = (role) => {
	const value = role?.is_system_default
	return value === true || value === 1 || value === '1'
}

/**
 * Company Roles Management Page (Company Admin)
 *
 * Role management for company admins:
 * - View system roles (read-only) - excludes super-admin
 * - Create/Edit/Delete company custom roles
 * - Clone system roles to create custom versions
 *
 * Features:
 * - System roles cannot be edited or deleted
 * - Custom roles fully manageable
 * - Clone functionality for both types
 */
const CompanyRoles = () => {
	const { user } = useAuth()
	const companyId = user?.company_id || 2 // Fallback for development

	// State
	const [roles, setRoles] = useState([])
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)
	const [selectedRole, setSelectedRole] = useState(null)

	// Filters
	const [searchTerm, setSearchTerm] = useState('')
	const [typeFilter, setTypeFilter] = useState('all')
	const [statusFilter, setStatusFilter] = useState('all')

	// Modal states
	const [showCreateModal, setShowCreateModal] = useState(false)
	const [showEditModal, setShowEditModal] = useState(false)
	const [showViewModal, setShowViewModal] = useState(false)
	const [showConfirmDialog, setShowConfirmDialog] = useState(false)
	const [actionType, setActionType] = useState(null)
	const [selectedRoleForAction, setSelectedRoleForAction] = useState(null)
	const [cloneData, setCloneData] = useState(null)

	// Fetch roles on mount
	useEffect(() => {
		fetchRoles()
	}, [companyId])

	/**
	 * Fetch company-assignable roles (excludes super-admin)
	 */
	const fetchRoles = async () => {
		setLoading(true)
		setError(null)
		try {
			const response = await roleService.getCompanyAssignableRoles(companyId)
			setRoles(response.data || [])
		} catch (err) {
			console.error('Failed to fetch roles:', err)
			setError('Failed to load roles')
			toast.error('Failed to load roles')
		} finally {
			setLoading(false)
		}
	}

	/**
	 * Filter roles based on search and filters
	 */
	const filteredRoles = useMemo(() => {
		return roles.filter((role) => {
			// Search filter
			if (searchTerm) {
				const search = searchTerm.toLowerCase()
				const matchesSearch =
					role.name?.toLowerCase().includes(search) ||
					role.slug?.toLowerCase().includes(search) ||
					role.description?.toLowerCase().includes(search)
				if (!matchesSearch) return false
			}

			// Type filter
			if (typeFilter !== 'all') {
				const isSysRole = isSystemRole(role)
				if (typeFilter === 'system' && !isSysRole) return false
				if (typeFilter === 'custom' && isSysRole) return false
			}

			// Status filter
			if (statusFilter !== 'all') {
				const isArchived = role.deleted_at || role.status === 'archived'
				if (statusFilter === 'active' && isArchived) return false
				if (statusFilter === 'archived' && !isArchived) return false
			}

			return true
		})
	}, [roles, searchTerm, typeFilter, statusFilter])

	/**
	 * Calculate stats
	 */
	const stats = useMemo(() => {
		const total = roles.length
		const system = roles.filter((r) => isSystemRole(r)).length
		const custom = roles.filter((r) => !isSystemRole(r)).length
		const archived = roles.filter((r) => r.deleted_at || r.status === 'archived').length

		return { total, system, custom, archived }
	}, [roles])

	// Handlers
	const handleCreate = () => {
		setSelectedRole(null)
		setShowCreateModal(true)
	}

	const handleView = (role) => {
		setSelectedRole(role)
		setShowViewModal(true)
	}

	const handleEdit = (role) => {
		setSelectedRole(role)
		setShowEditModal(true)
	}

	const handleDelete = (role) => {
		setSelectedRoleForAction(role)
		setActionType('delete')
		setShowConfirmDialog(true)
	}

	const handleRestore = (role) => {
		setSelectedRoleForAction(role)
		setActionType('restore')
		setShowConfirmDialog(true)
	}

	const handleClone = (role) => {
		const clonedData = {
			name: `${role.name} (Copy)`,
			company_id: companyId,
		}
		setCloneData(clonedData)
		setSelectedRoleForAction(role)
		setActionType('clone')
		setShowConfirmDialog(true)
	}

	/**
	 * Confirm action (delete/restore/clone)
	 */
	const confirmAction = async () => {
		if (!selectedRoleForAction) return

		setLoading(true)
		try {
			if (actionType === 'delete') {
				await roleService.delete(selectedRoleForAction.id)
				toast.success('Role archived successfully!')
			} else if (actionType === 'restore') {
				await roleService.restore(selectedRoleForAction.id)
				toast.success('Role restored successfully!')
			} else if (actionType === 'clone') {
				await roleService.clone(selectedRoleForAction.id, cloneData)
				toast.success('Role cloned successfully!')
			}

			// Refresh roles
			await fetchRoles()

			setShowConfirmDialog(false)
			setSelectedRoleForAction(null)
			setActionType(null)
			setCloneData(null)
		} catch (err) {
			console.error('Action failed:', err)
			toast.error(err.message || 'Action failed')
		} finally {
			setLoading(false)
		}
	}

	const cancelAction = () => {
		setShowConfirmDialog(false)
		setSelectedRoleForAction(null)
		setActionType(null)
		setCloneData(null)
	}

	/**
	 * Handle form submission success
	 */
	const handleFormSuccess = async () => {
		setShowCreateModal(false)
		setShowEditModal(false)
		setSelectedRole(null)
		await fetchRoles()
	}

	/**
	 * Get status badge
	 */
	const getStatusBadge = (role) => {
		if (role.deleted_at || role.status === 'archived') {
			return <span className="badge badge--archived">Archived</span>
		}
		return <span className="badge badge--active">Active</span>
	}

	/**
	 * Get type badge
	 */
	const getTypeBadge = (role) => {
		if (isSystemRole(role)) {
			return (
				<span className="badge badge--primary">
					<Lock size={12} />
					System
				</span>
			)
		}
		return <span className="badge badge--info">Custom</span>
	}

	// Table columns
	const columns = [
		{
			key: 'name',
			label: 'Role',
			sortable: true,
			render: (role) => (
				<div className="role-cell">
					<div className="role-cell__name">{role.name}</div>
					<div className="role-cell__slug">{role.slug}</div>
				</div>
			),
		},
		{
			key: 'type',
			label: 'Type',
			sortable: true,
			align: 'center',
			render: (role) => getTypeBadge(role),
		},
		{
			key: 'permissions',
			label: 'Permissions',
			align: 'center',
			render: (role) => {
				const permCount = role.permissions ? Object.keys(role.permissions).length : 0
				return `${permCount} resources`
			},
		},
		{
			key: 'status',
			label: 'Status',
			sortable: true,
			align: 'center',
			render: (role) => getStatusBadge(role),
		},
	]

	// Table actions
	const actions = [
		{
			label: 'View Details',
			icon: <Eye size={16} />,
			onClick: handleView,
		},
		{
			label: 'Edit',
			icon: <Edit2 size={16} />,
			onClick: handleEdit,
			show: (role) => !isSystemRole(role) && !role.deleted_at && role.status !== 'archived',
		},
		{
			label: 'Clone',
			icon: <Copy size={16} />,
			onClick: handleClone,
			variant: 'info',
			show: (role) => !role.deleted_at && role.status !== 'archived',
		},
		{
			label: 'Delete',
			icon: <Trash2 size={16} />,
			onClick: handleDelete,
			variant: 'danger',
			show: (role) => !isSystemRole(role) && !role.deleted_at && role.status !== 'archived',
		},
		{
			label: 'Restore',
			icon: <RotateCcw size={16} />,
			onClick: handleRestore,
			variant: 'primary',
			show: (role) => role.deleted_at || role.status === 'archived',
		},
	]

	return (
		<div className="company-roles-page">
			{/* Page Header */}
			<PageHeader
				title="Role Management"
				description="Manage user roles for your organization"
				breadcrumbs={[{ label: 'Settings' }, { label: 'Roles' }]}
				actions={
					<Button variant="primary" onClick={handleCreate}>
						<Plus size={20} />
						<span>Create Custom Role</span>
					</Button>
				}
			/>

			{/* Statistics */}
			<div className="stats-grid">
				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--primary">
						<Shield size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">Total Roles</div>
						<div className="stat-card__value">{stats.total}</div>
					</div>
				</div>

				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--success">
						<Lock size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">System Roles</div>
						<div className="stat-card__value">{stats.system}</div>
					</div>
				</div>

				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--info">
						<Users size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">Custom Roles</div>
						<div className="stat-card__value">{stats.custom}</div>
					</div>
				</div>

				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--secondary">
						<Filter size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">Archived</div>
						<div className="stat-card__value">{stats.archived}</div>
					</div>
				</div>
			</div>

			{/* Filters & Search */}
			<div className="filters-section">
				<SearchBar
					value={searchTerm}
					onChange={setSearchTerm}
					placeholder="Search roles..."
					loading={loading}
				/>

				<div className="filters-section__divider">
					<Filter size={18} />
				</div>

				<div className="filters-section__controls">
					<Select
						value={typeFilter}
						onChange={(e) => setTypeFilter(e.target.value)}
						options={[
							{ value: 'all', label: 'All Types' },
							{ value: 'system', label: 'System Roles' },
							{ value: 'custom', label: 'Custom Roles' },
						]}
					/>

					<div className="filters-section__sort-icon">
						<ArrowUpDown size={18} />
					</div>

					<Select
						value={statusFilter}
						onChange={(e) => setStatusFilter(e.target.value)}
						options={[
							{ value: 'all', label: 'All Status' },
							{ value: 'active', label: 'Active' },
							{ value: 'archived', label: 'Archived' },
						]}
					/>
				</div>
			</div>

			{/* Data Table */}
			<DataTable
				columns={columns}
				data={filteredRoles}
				actions={actions}
				loading={loading}
				emptyMessage="No roles found. Create a custom role to get started."
				onRowClick={handleView}
			/>

			{/* Create Modal */}
			{showCreateModal && (
				<CompanyRoleForm
					mode="create"
					companyId={companyId}
					onClose={() => setShowCreateModal(false)}
					onSuccess={handleFormSuccess}
				/>
			)}

			{/* Edit Modal */}
			{showEditModal && selectedRole && (
				<CompanyRoleForm
					mode="edit"
					companyId={companyId}
					roleData={selectedRole}
					onClose={() => {
						setShowEditModal(false)
						setSelectedRole(null)
					}}
					onSuccess={handleFormSuccess}
				/>
			)}

			{/* View Modal */}
			{showViewModal && selectedRole && (
				<CompanyRoleView
					roleData={selectedRole}
					onClose={() => {
						setShowViewModal(false)
						setSelectedRole(null)
					}}
					onEdit={() => {
						setShowViewModal(false)
						setShowEditModal(true)
					}}
					onClone={() => {
						setShowViewModal(false)
						handleClone(selectedRole)
					}}
					onDelete={() => {
						setShowViewModal(false)
						handleDelete(selectedRole)
					}}
				/>
			)}

			{/* Confirm Dialog */}
			{showConfirmDialog && selectedRoleForAction && (
				<ConfirmDialog
					isOpen={true}
					onConfirm={confirmAction}
					onCancel={cancelAction}
					title={
						actionType === 'delete'
							? 'Archive Role?'
							: actionType === 'restore'
								? 'Restore Role?'
								: 'Clone Role?'
					}
					message={
						actionType === 'delete'
							? `Are you sure you want to archive "${selectedRoleForAction.name}"? Users with this role will need to be reassigned.`
							: actionType === 'restore'
								? `Are you sure you want to restore "${selectedRoleForAction.name}"?`
								: `Clone "${selectedRoleForAction.name}" as "${cloneData?.name}"? This creates a new custom role with the same permissions.`
					}
					confirmText={actionType === 'delete' ? 'Archive' : actionType === 'restore' ? 'Restore' : 'Clone'}
					variant={actionType === 'delete' ? 'danger' : actionType === 'restore' ? 'info' : 'primary'}
				/>
			)}
		</div>
	)
}

export default CompanyRoles
